import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Divider, Space, Flex, Image } from 'antd';
import { BaseContainer, TinyModal, SearchDesc, AlertModal } from 'components/containers';
import { DataGrid, FillButton, OutlinedButton, RoundTag , ExcelButton } from 'components/elements';
import { GridSetting } from 'pages/common';
import { getListAPI, getBookCategoryAPI } from 'api/common';
import { deleteShoppingBookAPI, putShoppingBookSortAPI } from 'api/shopping';
import { fnSetColumn, objToLowerCase, getAmountWithComma } from 'utils/utils';
import { ProductBookTypeOptions, productDisplayStatusOptions } from 'data/options';
import { URL_ADMIN, URL_SHOPPING, URL_SHOPPING_BOOK_ADD, URL_SHOPPING_BOOK_EDIT } from 'routes/AdminRoute';
import { useCommonContext } from 'hooks/CommonContext';
import { BaseSection } from 'components/layouts';

const ShoppingBookList = () => {
  const { sharedData } = useCommonContext();
  const { updateSharedData } = useCommonContext();
  const [dataList, setDataList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [category2List, setCategory2List] = useState([]);
  const [searchData, setSearchData] = useState({
    category: sharedData?.category1?.idx || '',
    sub_category: sharedData?.category2?.idx || ''
  });
  const [rowChecked, setRowChecked] = useState([]);
  const [columns, setColumns] = useState([]);
  const [gridSetting, setGridSetting] = useState([]);
  const [stats, setStats] = useState([]);
  const [bookDetail, setBookDetail] = useState({});
  const navigate = useNavigate();
  const [modal, setModal] = useState({ sDetail: false, isDelivery: false });

  const [alertModal, setAlertModal] = useState({
    open: false,
    message: ''
  });

  // alert 모달 닫기
  const fnCloseAlertModal = () => {
    setAlertModal({ ...alertModal, open: false, message: '' });
  };

  // 교재내용 모달
  const fnBookDetailModal = (record) => {
    setBookDetail({ ...bookDetail, product_thumbnail_path: record?.product_thumbnail_path, product_detail_content: record?.product_detail_content });
    setModal({ ...modal, isDetail: true });
  };

  // 정렬 순서변경
  const swapItems = async (idx , sortNo, offset) => {
    const targetSortNo = sortNo + offset;
    const lastSortNo = dataList?.[dataList?.length-1]?.sort_no

    if (targetSortNo > 0 && targetSortNo <= lastSortNo) {
      try {
        const body = {
          idx,
          currentSortNo: sortNo,
          targetSortNo
        }
        await putShoppingBookSortAPI(body)
        fnGetList()
      } catch (err) {
        console.error(err)
      }
    } else {
      setAlertModal({ ...alertModal, open: true, message: '이동할 수 있는 부분이 없거나 범위를 넘었습니다.' });
    }
  };

  //표설정 변경시 테이블 항목 업데이트
  const fnUpdateGridSetting = (v) => {
    setColumns(fnSetColumn(v, column));
    setGridSetting(v);
  };

  // 목록 삭제
  const fnDeleteList = async () => {
    try {
      if (rowChecked <= 0) {
        setAlertModal({ ...alertModal, open: true, message: '삭제할 목록을 체크해주세요.' });
        return;
      }

      const { data } = await deleteShoppingBookAPI(rowChecked);
      if (data?.responseCode === 200) alert('삭제되었습니다.');
      else alert('다시 시도해주세요.')
      fnGetList();
    } catch (error) {
      console.error(error);
    }
  };

  // 카테고리 목록 가져오기
  const fnGetBookCategory = async () => {
    try {
      const params = { book_cate1_code_fg: 1 };
      const result = await getBookCategoryAPI(params);
      if (result?.status === 200) {
        const process = result?.data?.data_list?.map((item) => {
          const toLow = objToLowerCase(item);
          return {
            ...toLow,
            key: toLow?.book_cate1_code,
            title: toLow?.book_cate1
          };
        });
        setCategoryList([...process, {key: '0040000', title: '기타/물품'}]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (searchData?.category) {
        fnGetBookCategory2(searchData?.category);
      }
    }
  };

  // 2차 카테고리 목록 가져오기
  const fnGetBookCategory2 = async (categoryCode) => {
    try {
      const params = { book_cate1_code: categoryCode, book_cate1_code_fg: 1 };
      const result = await getBookCategoryAPI(params);
      if (result?.status === 200) {
        const process = result?.data?.data_list?.map((item) => {
          const toLow = objToLowerCase(item);
          return {
            ...toLow,
            key: toLow?.book_cate2_code,
            title: toLow?.book_cate2
          };
        });
        setCategory2List(process);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 초기 리스트 호출
  const fnGetList = async () => {
    try {
      const params = {
        book_cate1_code_fg: 1,
        book_cate1_code: searchData?.category,
        book_cate2_code: searchData?.sub_category
      };
      const res = await getListAPI(params);
      if (res?.status === 200) {
        const { COLUMS_LIST, DATA_LIST, STATICS_LIST } = res?.data;
        setStats(STATICS_LIST);
        setColumns(fnSetColumn(COLUMS_LIST, column));
        setDataList(DATA_LIST?.map((it) => ({ ...objToLowerCase(it) })));
        setGridSetting(COLUMS_LIST);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const apiCall = async () => {
      await fnGetBookCategory();
       fnGetList();
    };
    apiCall();
  }, [searchData]);

  const items = [
    {
      key: 1,
      label: '1차 카테고리 선택',
      children: (
        <Flex gap="small" wrap="wrap">
          {categoryList?.map((it) => (
            <span
              key={it.key}
              className="text-click"
              onClick={() => {
                if (searchData?.category === it?.key) {
                  setSearchData({ ...searchData, category: '', sub_category: '' });
                  updateSharedData({
                    key: 'category1',
                    newData: {idx: ''}
                  })
                } else {
                  setSearchData({ ...searchData, category: it.key, sub_category: '' });
                  updateSharedData({
                    key: 'category1',
                    newData: { idx: it.key }
                  });
                  fnGetBookCategory2(it.key);

                }
              }}
            >
              <RoundTag label={it.title} color={searchData?.category === it.key ? 'var(--color-blue01)' : 'blue'} />
            </span>
          ))}
        </Flex>
      )
    },
    ...(searchData?.category
      ? [
          {
            key: 2,
            label: '2차 카테고리 선택',
            children: (
              <Flex gap="small" wrap="wrap">
                {category2List?.map((it) => (
                  <span
                    key={it.key}
                    className="text-click"
                    onClick={() => {
                      if (searchData?.sub_category === it?.key) {
                        setSearchData({ ...searchData, sub_category: '' });
                        updateSharedData({
                          key: 'category2',
                          newData: {idx: ''}
                        })
                      } else {
                        setSearchData({ ...searchData, sub_category: it.key });
                        updateSharedData({
                          key: 'category2',
                          newData: { idx: it.key }
                        });
                      }
                    }}
                  >
                    <RoundTag label={it.title} color={searchData?.sub_category === it.key ? 'var(--color-blue01)' : 'blue'} />
                  </span>
                ))}
              </Flex>
            )
          }
        ]
      : [])
  ];

  const column = [
    {
      title: '이미지',
      align: 'center',
      dataIndex: 'product_thumbnail_path',
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      },
      render: (text, record) => {
        return text ? <Image width={80} src={text} /> : <Image width={80} src={`/assets/images/no_image.png`} />;
      }
    },
    {
      title: '상품코드',
      align: 'center',
      dataIndex: 'product_cd',
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      }
    },
    {
      title: '상품명',
      align: 'center',
      dataIndex: 'product_name',
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      }
    },
    {
      title: '상품유형',
      align: 'center',
      dataIndex: 'product_book_type',
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      },
      render: (text) => ProductBookTypeOptions.find((it) => it.value === text)?.label
    },
    {
      title: '카테고리',
      align: 'center',
      dataIndex: 'category',
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      },
      render: (text, record) => {
        const book_cate1 = record?.book_cate1?.split(';');
        const book_cate2 = record?.book_cate2?.split(';');
        return (
          <Space>
            <Flex vertical={true} align="start">
              {book_cate1?.map((it, i) => (
                <>
                  <div>
                    {book_cate1?.[i]}
                    {`>`}
                    {book_cate2?.[i]}
                  </div>
                </>
              ))}
            </Flex>
            <div style={{ paddingLeft: '12px' }}>
              <OutlinedButton label="교재내용" color={'var(--color-blue01)'} onClick={() => fnBookDetailModal(record)} />
            </div>
          </Space>
        );
      }
    },
    {
      title: '다음 과정',
      align: 'center',
      dataIndex: 'next_curriculum',
      width: 200,
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      },
      render: (text) => text !== '-1' && text
    },
    {
      title: '노출여부',
      align: 'center',
      dataIndex: 'display_yn',
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      },
      render: (text) => productDisplayStatusOptions.find((it) => it.value === text)?.label
    },
    {
      title: '판매금액',
      align: 'center',
      dataIndex: 'basic_price',
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      },
      render: (text) => (text ? getAmountWithComma(text) + '원' : '0원')
    },
    {
      title: '지사수수료',
      align: 'center',
      dataIndex: 'branch_charge',
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      },
      render: (text,record) => (<>
        {text ? text : '0'}%
        <br />
        ({text ? getAmountWithComma(record.basic_price * (text/100) ) : '0'}원)
      </>
    )
    },
    {
      title: '정렬순서',
      align: 'center',
      dataIndex: 'sort_no',
      render: (text, record, index) => {
        const first = dataList?.[0]?.sort_no === text
        const last = dataList?.[dataList?.length-1]?.sort_no === text;
        return (
          <Space>
            <Flex vertical align="center" className="text-click">
              {!first && (
                <div id="prev" style={{ width: '20px', height: '20px' }} onClick={() => swapItems(record.idx, text, -1)}>
                  <Image src="/assets/images/single-arrow-top.png" width="20px" preview={false} />
                </div>
              )}
              {!last && (
                <div id="next" style={{ width: '20px', height: '20px' }} onClick={() => swapItems(record.idx, text, 1)}>
                  <Image id="next" src="/assets/images/single-arrow-bottom.png" width="20px" preview={false} />
                </div>
              )}
            </Flex>
            <Flex vertical align="center" className="text-click">
              {!first && (
                <div id="prev5" style={{ width: '20px', height: '20px' }} onClick={() => swapItems(record.idx, text, -5)}>
                  <Image src="/assets/images/duoble-arrow-top.png" width="20px" preview={false} />
                </div>
              )}
              {!last && (
                <div id="next5" style={{ width: '20px', height: '20px' }} onClick={() => swapItems(record.idx, text, 5)}>
                  <Image src="/assets/images/duoble-arrow-bottom.png" width="20px" preview={false} />
                </div>
              )}
            </Flex>
          </Space>
        );
      }
    },
    {
      title: '수정',
      align: 'center',
      dataIndex: 'col11',
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          }
        };
      },
      render: (item, record) => {
        return <FillButton label="수정" backgroundColor={'var(--color-blue01)'} onClick={() => navigate(`${URL_ADMIN + URL_SHOPPING + URL_SHOPPING_BOOK_EDIT.replace(':no', record.product_cd)}`)} />;
      }
    }
  ];

  const rowSelection = {
    type: 'checkbox',
    selectedRowKeys: rowChecked,
    onChange: (selectedRowKeys, selectedRows) => {
      setRowChecked(selectedRowKeys);
    }
  };

  return (
    <>
    <BaseSection>
      <SearchDesc inline stats={stats} items={items} column={1} />
      <BaseContainer>
        <Row>
          <Col span={8}>
            <Space>
              <GridSetting colList={gridSetting} fnUpdateCol={fnUpdateGridSetting} />
              <ExcelButton
                // label={'엑셀 다운로드'} //뷰네임 미입력시 기본값
                // isCheckComp={} //체크박스 컴포넌트 여부 미입력시 X
                headerList={column} //필수 헤더리스트 표 설정과 동일한 변수여야함
                gridList={dataList} //필수 실시간 그리드 데이터
                // filterList={} //뺄 칼럼 목록 ex) 상세보기 미입력시 전체
                // codeList={} //코드로 바꿔서 다운할 목록 미입력시 코드밸류 그대로
                // checkRows={} //isCheckComp true 일시 checkRows 데이터 미입력시 체크작동안함
                // checkKey={} //isCheckComp true 일시 check 한 데이터 dataIndex 키 네임 미입력시 체크작동안함
              />
            </Space>
          </Col>
          <Col flex="auto">
            <Flex justify="flex-end">
              <Space>
                <OutlinedButton label="삭제" color={'var(--color-red04'} onClick={fnDeleteList} />
                <FillButton label="등록" backgroundColor="var(--color-gray03)" onClick={() => navigate(`${URL_ADMIN + URL_SHOPPING + URL_SHOPPING_BOOK_ADD}`)} />
              </Space>
            </Flex>
          </Col>
        </Row>
        <Divider className="divider-small" />
        <DataGrid
          rowKey={'idx'}
          columns={column}
          data={dataList}
          pagination
          rowSelection={{
            ...rowSelection
          }}
        />
      </BaseContainer>

      <TinyModal title={'상품 내용'} open={modal?.isDetail} width={'800px'} onClose={() => setModal({ ...modal, isDetail: false })}>
        <div>
          <div>{bookDetail?.product_thumbnail_path && <Image width={160} src={bookDetail?.product_thumbnail_path} />}</div>
          <div dangerouslySetInnerHTML={{ __html: bookDetail?.product_detail_content }} />
        </div>
      </TinyModal>
      {/* AlertModal */}
      <AlertModal open={alertModal?.open} onCancel={fnCloseAlertModal}>
        {alertModal?.message}
      </AlertModal>
    </BaseSection>
    </>
  );
};

export default ShoppingBookList;
